import type { InfiniteData } from "@tanstack/react-query"
import { useCallback, useMemo } from "react"
import {
  ItemType,
  type TableItem,
  type TablePlaceholderItem,
} from "./infinite-table"

type TPage<TItem> = { items: TItem[] }

interface InfiniteQueryResult<TItem> {
  pages: InfiniteData<TPage<TItem>>["pages"]
  hasNextPage: boolean | undefined
  isFetchingNextPage: boolean
  fetchNextPage: () => Promise<unknown>
}

interface UseInfiniteItemsResult<TItem> {
  items: TableItem<TItem>[]
  loadNext: () => void
}

function createPlaceholders(count = 5): TablePlaceholderItem[] {
  return Array.from({ length: count }).map((_, index) => ({
    type: ItemType.PLACEHOLDER,
    id: `placeholder-${index}`,
  }))
}

export function useInfiniteTableItems<TItem>(
  queryResult: InfiniteQueryResult<TItem>,
): UseInfiniteItemsResult<TItem> {
  const { pages, hasNextPage, isFetchingNextPage, fetchNextPage } = queryResult

  // TODO: implement proper validation
  const hasMoreItems =
    pages.length > 0 && pages[pages.length - 1].items.length > 10

  const items = useMemo(
    () =>
      pages.flatMap((page) =>
        page.items.map((item) => ({
          type: ItemType.CONTENT,
          content: item,
        })),
      ),
    [pages],
  )

  const allItems = useMemo(() => {
    return isFetchingNextPage ? [...items, ...createPlaceholders()] : items
  }, [isFetchingNextPage, items])

  const loadNext = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && hasMoreItems) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, hasMoreItems])

  return useMemo(() => ({ items: allItems, loadNext }), [allItems, loadNext])
}
